import React from 'react';

export interface UsePollingOptions {
  /** 要轮询执行的异步函数。
   *  如果返回 `true`，则自动停止轮询。
   *  抛出错误会被计入失败次数。
   */
  fn: () => Promise<boolean | void>;

  /** 每次轮询间隔（毫秒），默认为 1500 */
  interval?: number;

  /** 超时时间（毫秒），默认为 0 */
  timeout?: number;

  /** 允许的最大连续失败次数，超过后停止轮询，默认 3 */
  maxFails?: number;

  /** 每次请求失败时的回调（非致命错误） */
  onError?: (error: string) => void;

  /** 超时回调 */
  onTimeout?: () => void;

  /** 成功或失败停止轮询时的回调 */
  onFinish?: () => void;
}

export interface UsePollingResult {
  /** 启动轮询 */
  start: () => void;

  /** 停止轮询 */
  stop: () => void;

  /** 当前是否在轮询中 */
  isRunning: boolean;
}

export function usePolling({
  fn,
  interval = 3000,
  timeout = 0,
  maxFails = 3,
  onError,
  onTimeout,
  onFinish,
}: UsePollingOptions): UsePollingResult {
  const [isRunning, setIsRunning] = React.useState(false);

  const stoppedRef = React.useRef(true);
  const failRef = React.useRef(0);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const stop = React.useCallback(() => {
    stoppedRef.current = true;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsRunning(false);
    onFinish?.();
  }, []);

  const loop = React.useCallback(async () => {
    if (stoppedRef.current) return;
    try {
      const shouldStop = await fn();
      if (shouldStop) {
        stop();
        return;
      }
      failRef.current = 0;
    } catch (err: any) {
      failRef.current += 1;
      if (failRef.current >= maxFails) {
        onError?.(err?.response?.data?.detail || err?.message || '');
        stop();
        return;
      }
    }
    if (!stoppedRef.current) {
      setTimeout(loop, interval);
    }
  }, [fn]);

  const start = React.useCallback(() => {
    if (isRunning) return;
    stoppedRef.current = false;
    setIsRunning(true);
    failRef.current = 0;
    if (timeout > 0) {
      timeoutRef.current = setTimeout(() => {
        if (!stoppedRef.current) {
          stop();
          onTimeout?.();
        }
      }, timeout);
    }
    setTimeout(loop, interval);
  }, [isRunning, loop]);

  React.useEffect(() => {
    return () => stop();
  }, [stop]);

  return { start, stop, isRunning };
}
